import { useEffect, useState } from 'react'
import { FieldValues, UseFormSetValue } from 'react-hook-form'

import { Box } from '@mui/material'
import { DataService } from 'api/DataService'
import PayeeForm from 'templates/AccountsPayable/components/ConfigurationDrawer/Steps/PayeeForm'

import AutocompleteAdd from 'components/AutocompleteAdd/AutocompleteAdd'

import { theme } from 'styles/theme'

type Record = {
  label: string
  value: string
  payee_type: string
}

type PayeeAutocompleteProps<T extends FieldValues> = {
  value: Omit<Record, 'payee_type'> | null
  onChange: any
  setValue?: UseFormSetValue<T>
  errorMessage?: string
  showAddButton?: boolean
}

const PayeeAutocomplete = <T extends FieldValues>({
  value,
  onChange,
  setValue,
  errorMessage,
  showAddButton = true
}: PayeeAutocompleteProps<T>) => {
  const [payees, setPayees] = useState<Record[]>()
  // Incrementa depois de criar um item novo pelo "Adicionar", pra lista ser rebuscada
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const fetchPayeesAutocomplete = async () => {
      const response = await DataService({
        type: 'GET',
        url: '/v1/accounts_payables/payees/autocomplete'
      })

      const records = response.data.records.map(
        (payee: { name: any; uuid: any; payee_type: any }) => ({
          label: payee.name,
          value: payee.uuid,
          payee_type: payee.payee_type
        })
      )

      // Os quatro valores de `payee_type` (AccountsPayablePayee). O enum de
      // ThirdParty tem sete, e os três extras (investor, tenant,
      // service_provider) nunca casariam com um beneficiário de conta a pagar.
      const typeGroups = [
        { type: 'supplier', label: 'Fornecedores' },
        { type: 'employee', label: 'Funcionários' },
        { type: 'partner', label: 'Sócios' },
        { type: 'other', label: 'Outros' }
      ]

      const groupedPayees: Record[] = []
      typeGroups.forEach(({ type, label }) => {
        const filtered = records.filter(
          (record: Record) => record.payee_type === type
        )
        if (filtered.length > 0) {
          if (showAddButton) {
            groupedPayees.push({ label, value: type, payee_type: type })
          }
          groupedPayees.push(...filtered)
        }
      })

      setPayees(groupedPayees)
    }

    fetchPayeesAutocomplete()
  }, [reloadKey])
  return (
    <>
      <AutocompleteAdd
        showAddButton={showAddButton}
        addPermission="accounts_payable:manage"
        key={value?.value}
        {...{ value, onChange, setValue, errorMessage }}
        addButtonText="Adicionar beneficiário"
        label="Beneficiário *"
        placeholder="Selecione o beneficiário"
        modalTitle="Novo beneficiário"
        options={payees || []}
        getOptionDisabled={(option: any) =>
          [
            'supplier',
            'employee',
            'partner',
            'investor',
            'tenant',
            'service_provider',
            'other'
          ].includes(option.value)
        }
        renderOption={(props, option) => {
          const optionTyped = option as Record
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { style, sx, onClick, ...rest } = props

          if (
            [
              'supplier',
              'employee',
              'partner',
              'investor',
              'tenant',
              'service_provider',
              'other'
            ].includes(optionTyped.value)
          ) {
            return (
              <Box
                component="li"
                sx={{
                  all: 'unset',
                  bgcolor: theme.palette.neutral[700],
                  fontWeight: 600,
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}
                aria-disabled={true}
                data-option-index={-1}
                {...rest}
              >
                {optionTyped.label}
              </Box>
            )
          }

          return (
            <Box
              component="li"
              sx={{ all: 'unset' }}
              onClick={onClick}
              {...rest}
            >
              {optionTyped.label}
            </Box>
          )
        }}
        modalBodyComponent={
          <PayeeForm
            onCreated={(item) => {
              // seleciona o item novo no campo de quem usa o autocomplete
              onChange(null, { label: item.name, value: item.uuid })
              setReloadKey((key) => key + 1)
            }}
            handleClose={() => {
              return
            }}
          />
        }
      />
    </>
  )
}

export default PayeeAutocomplete
